"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.SelectComboBox = exports.PLACEHOLDER_MARGIN_WITH_CONTENTBEFORE = exports.PLACEHOLDER_MARGIN_DEFAULT = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _core = require("@emotion/core");
var _web = require("@react-hookz/web");
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _downshift = require("downshift");
var _react = require("react");
var _useTheme = require("../../../../theme/utils/useTheme");
var _useHovered = require("../../../../util/useHovered");
var _focusMode = require("../../../focusMode");
var _formInputTheme = require("../../../textinput/styled/formInputTheme");
var _useA11yStatusMessages = require("../../hooks/useA11yStatusMessages");
var _useAutoHighlightWithLoading = require("../../hooks/useAutoHighlightWithLoading");
var _useHorizontalResize = require("../../hooks/useHorizontalResize");
var _useSelectFlyout = require("../../hooks/useSelectFlyout");
var _highlightFirstAutomaticallyStateReducer = require("../../reducers/highlightFirstAutomaticallyStateReducer");
var _utils = require("../../utils");
var _selectV2Theme = require("../selectV2Theme");
var _Actions = require("../shared/Actions");
var _HiddenInput = require("../shared/HiddenInput");
var _Root = require("../shared/Root");
var _SelectClearButton = require("../shared/SelectClearButton");
var _SelectMenu = require("../shared/SelectMenu");
var _SelectPlaceholder = require("../shared/SelectPlaceholder");
var _ToggleButton = require("../shared/ToggleButton");
var _ComboBoxInput = require("./ComboBoxInput");
var _ComboBoxTextArea = require("./ComboBoxTextArea");
var _ContentBeforeContainer = require("./ContentBeforeContainer");
var _SelectedOptionFacade = require("./SelectedOptionFacade");
var _SelectedValueWrapper = require("./SelectedValueWrapper");
/** @jsxRuntime classic */
/** @jsx jsx */

const warnOnTempFormatSelectedValueWrapperFixUnused = (0, _lodestarUtils.warnOnce)("SelectV2 (combobox): If you're using formatSelectedValue, you should also be using" + " tempFormatSelectedValueWrapperFix={true} to apply the fix for SelectV2 not" + " properly applying `position: absolute` to the wrapper. This prop and warning will be" + " removed in a future major version release and the CSS fix will be applied by default.");
const PLACEHOLDER_MARGIN_DEFAULT = 12;
exports.PLACEHOLDER_MARGIN_DEFAULT = PLACEHOLDER_MARGIN_DEFAULT;
const PLACEHOLDER_MARGIN_WITH_CONTENTBEFORE = 40;
exports.PLACEHOLDER_MARGIN_WITH_CONTENTBEFORE = PLACEHOLDER_MARGIN_WITH_CONTENTBEFORE;
const SelectComboBox = _ref => {
  let {
    appearance = "outlined",
    "aria-describedby": ariaDescribedby,
    "aria-labelledby": ariaLabelledby,
    "aria-required": ariaRequired,
    id,
    onChange,
    options,
    selectedOption,
    // optional below
    "aria-label": ariaLabel,
    classes,
    className,
    clientId,
    formatOption,
    getHiddenInputValue,
    hiddenInputName,
    innerRef,
    inputCharacterLimit,
    inputStyle = {},
    isLoading,
    isOptionDisabled,
    isReadonly,
    isClearable,
    menuWidth = "match-control",
    maxMenuHeight,
    maxOptionsUntilVirtualization,
    menuId,
    menuPlacement = "bottom",
    menuPosition,
    onBlur,
    onCreateOption,
    onFocus,
    optionHeight,
    optionToString,
    portalProps,
    filterOption,
    getOptionKey,
    isMultiline,
    validationState,
    menuIsOpen,
    shouldResetFiltering = () => true,
    shouldContentBeforePersist = false,
    setMenuIsOpen,
    onMenuIsOpenChanged,
    inputValue: providedInputValue,
    setInputValue: providedSetInputValue,
    shouldFilterInitialInput = false,
    hideDropdownArrow = false,
    formatSelectedValue,
    placeholder,
    contentBefore,
    onKeyDown: providedOnKeyDown,
    highlightFirstOptionAutomatically = false,
    onPaste: providedOnPaste,
    tempFormatSelectedValueWrapperFix = false,
    inputFieldHeight
  } = _ref;
  if (process.env.NODE_ENV !== "production") {
    if (formatSelectedValue !== undefined && !tempFormatSelectedValueWrapperFix) {
      warnOnTempFormatSelectedValueWrapperFixUnused();
    }
  }
  const a11yMessages = (0, _useA11yStatusMessages.useA11yStatusMessages)();
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  const isCreatable = typeof onCreateOption === "function";
  const isInteractive = !isReadonly;
  const [inputValue, setInputValue] = (0, _react.useState)(providedInputValue ?? "");
  const [isFocused, setIsFocused] = (0, _react.useState)(false);
  const [filteredOptions, setFilteredOptions] = (0, _react.useState)(null);
  const {
    ref: resizeRef,
    width: targetWidth
  } = (0, _useHorizontalResize.useHorizontalResize)();
  const localContainerRef = (0, _react.useRef)(null);
  const containerRef = (0, _lodestarUtils.useForkRef)(localContainerRef, resizeRef);
  const localInputRef = (0, _react.useRef)(null);
  const toggleButtonRef = (0, _react.useRef)(null);
  const isKeyboardNavigation = (0, _react.useRef)(false);
  const {
    isHovered: isToggleHovered,
    onMouseEnter: onToggleMouseEnter,
    onMouseLeave: onToggleMouseLeave
  } = (0, _useHovered.useHovered)();
  const {
    isActive: isToggleActive,
    onMouseDown: onTAMouseDown,
    onMouseEnter: onTAMouseEnter,
    onMouseLeave: onTAMouseLeave
  } = (0, _lodestarUtils.useActive)({
    onMouseEnter: onToggleMouseEnter,
    onMouseLeave: onToggleMouseLeave
  });
  const optionsToDisplay = filteredOptions || options;
  const resetFiltering = reason => {
    if (!shouldResetFiltering({
      highlightedIndex,
      // pass optionsToDisplay to match highlightedIndex
      options: optionsToDisplay
    }, reason)) {
      return;
    }
    setInputValue("");
    providedSetInputValue?.("");
    setFilteredOptions(null);
  };
  const doFilter = (0, _react.useCallback)(forInputValue => {
    if (!forInputValue) {
      return;
    }
    const inputLowerCase = forInputValue?.trim().toLowerCase() ?? "";
    const fuzzySearchScoreMap = new Map();
    const filteredItems = options.filter(opt => {
      if ((0, _utils.isSelectV2OptionSeparator)(opt)) {
        return true;
      }
      return filterOption(opt, inputLowerCase, fuzzySearchScoreMap);
    });
    (0, _utils.sortFilteredItems)(fuzzySearchScoreMap, filteredItems);
    const squashedAndFilteredItems = (0, _utils.squashSeparators)(filteredItems);
    if (isCreatable) {
      if (squashedAndFilteredItems.filter(option => optionToString(option).toLowerCase() === inputLowerCase).length) {
        setFilteredOptions(squashedAndFilteredItems.filter(item => item.value !== _utils.creatableMarkerOption.value));
        return;
      }
      setFilteredOptions([_utils.creatableMarkerOption, ...squashedAndFilteredItems]);
    } else {
      setFilteredOptions(squashedAndFilteredItems);
    }
  }, [filterOption, isCreatable, optionToString, options]);
  const [valueFormatWrapperWidth, setValueFormatWrapperWidth] = (0, _react.useState)(undefined);
  const {
    ref: inputResizeObserverRef
  } = (0, _lodestarUtils.useResizeObserver)((0, _react.useCallback)(entries => {
    const inputWidth = entries[0].contentRect.width;
    setValueFormatWrapperWidth(inputWidth);
  }, []));
  const forkedInputRef = (0, _lodestarUtils.useForkRef)(localInputRef, inputResizeObserverRef, innerRef || null);
  const {
    isOpen,
    closeMenu,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
    toggleMenu,
    setHighlightedIndex
  } = (0, _downshift.useCombobox)({
    ...a11yMessages,
    ...(0, _utils.useSelectIds)("combobox", id),
    ...(menuIsOpen !== undefined ? {
      isOpen: menuIsOpen
    } : null),
    circularNavigation: false,
    inputValue,
    items: optionsToDisplay,
    itemToString: item => item === null ? "" : optionToString(item),
    selectedItem: selectedOption,
    onSelectedItemChange: _ref2 => {
      let {
        selectedItem
      } = _ref2;
      return onChange(selectedItem ?? null);
    },
    onInputValueChange: _ref3 => {
      let {
        inputValue,
        isOpen
      } = _ref3;
      // Don't try and filter when the menu is closed. This can be triggered by
      // the selection changing.
      if (isOpen && inputValue) {
        doFilter(inputValue);
      } else {
        resetFiltering("closed-menu-input-value-change");
      }
    },
    stateReducer: (currentState, actionAndChanges) => {
      const {
        type,
        changes
      } = actionAndChanges;
      switch (type) {
        case _downshift.useCombobox.stateChangeTypes.InputKeyDownEscape:
          // Cancel the escape key handling. We want to handle this key on our own
          // through the StackOrderManager.
          return currentState;
        case _downshift.useCombobox.stateChangeTypes.InputKeyDownEnter:
        case _downshift.useCombobox.stateChangeTypes.ItemClick:
          if (changes.selectedItem) {
            return isOptionDisabled?.(changes.selectedItem) ? currentState : changes;
          }
          break;
        // If no results are found and a user closes the menu (either by clicking)
        // the toggle button, the "no results" message, or blur, we want to reset.
        case _downshift.useCombobox.stateChangeTypes.ToggleButtonClick:
        case _downshift.useCombobox.stateChangeTypes.FunctionToggleMenu:
          if (!selectedItem) {
            resetFiltering("toggle-menu");
          }
      }
      if (changes.highlightedIndex) {
        switch (type) {
          case _downshift.useCombobox.stateChangeTypes.InputKeyDownArrowUp:
          case _downshift.useCombobox.stateChangeTypes.InputKeyDownArrowDown:
            if ((0, _utils.isSelectV2OptionSeparator)(optionsToDisplay[changes.highlightedIndex])) {
              return {
                ...changes,
                highlightedIndex: (0, _utils.findNextValidOptionIndex)(options, changes.highlightedIndex, type === _downshift.useCombobox.stateChangeTypes.InputKeyDownArrowDown)
              };
            }
        }
      }
      if (highlightFirstOptionAutomatically) {
        return (0, _highlightFirstAutomaticallyStateReducer.highlightFirstAutomaticallyStateReducer)(optionsToDisplay, currentState, actionAndChanges);
      }
      return changes;
    },
    onStateChange: changes => {
      switch (changes.type) {
        case _downshift.useCombobox.stateChangeTypes.InputKeyDownEnter:
        case _downshift.useCombobox.stateChangeTypes.ItemClick:
          if (changes.selectedItem) {
            if (isCreatable && (0, _utils.isCreatableMarkerOption)(changes.selectedItem) && inputValue) {
              onCreateOption(inputValue);
            }
          }
          // if changes.selectedItem is undefined, it means the user clicked
          // the same menu item twice
          else if (!changes.selectedItem && selectedItem) {
            onChange(selectedItem);
          }
          // For any selection, clear things out.
          resetFiltering("option-selected");
          break;
        case _downshift.useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
          // Clear filtering on initial state based on controlled props
          resetFiltering("initial-input-value");
          break;
        default:
          break;
      }
    },
    onIsOpenChange: _ref4 => {
      let {
        type,
        isOpen: openState
      } = _ref4;
      if (process.env.NODE_ENV !== "production") {
        warnOnceForDeprecatedSetMenuIsOpenProp = warnOnceForDeprecatedSetMenuIsOpenProp ?? (0, _lodestarUtils.warnOnce)("SelectV2 (combobox): setMenuIsOpen is deprecated. Please use onMenuIsOpenChanged instead.");
        if (setMenuIsOpen) {
          warnOnceForDeprecatedSetMenuIsOpenProp();
        }
      }
      // setMenuIsOpen is deprecated. Use onMenuIsOpenChanged if it's available instead.
      (onMenuIsOpenChanged ?? setMenuIsOpen)?.(!!openState);
      switch (type) {
        case _downshift.useCombobox.stateChangeTypes.InputBlur:
        case _downshift.useCombobox.stateChangeTypes.FunctionToggleMenu:
          resetFiltering("input-blur");
          break;
      }
    }
  });
  const {
    flyoutProps,
    targetRef,
    ariaOwns,
    ariaControls
  } = (0, _useSelectFlyout.useSelectFlyout)({
    menuId,
    clientId,
    closeMenu,
    isOpen,
    targetRef: containerRef,
    menuPlacement,
    menuPosition,
    portalProps,
    updateKey: isMultiline ? `${String(isOpen)}-${inputValue}` : String(isOpen)
  });
  const commonKeyDownHandler = e => {
    providedOnKeyDown?.(e, {
      highlightedIndex,
      // pass optionsToDisplay to match highlightedIndex
      options: optionsToDisplay
    });
    switch (e.key) {
      case "Tab":
        if (isOpen) {
          e.preventDefault();
          localInputRef.current?.focus();
          closeMenu();
          resetFiltering("key-down-tab");
        }
        break;
      case "Escape":
        resetFiltering("key-down-escape");
    }
  };
  const downshiftInputProps = getInputProps({
    value: inputValue,
    "aria-describedby": ariaDescribedby,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-required": ariaRequired,
    onPaste: providedOnPaste,
    onKeyDown: commonKeyDownHandler,
    ref: forkedInputRef,
    "aria-controls": ariaControls,
    /**
     * inputValue has to be updated here due updates in different ticks as normal behavior.
     * Setting inputValue here avoids cursor resetting after input value modifications.
     */
    onChange: (0, _react.useCallback)(e => {
      const val = e.target.value;
      const validInput =
      // Account for multi-byte characters by using `[].length` instead of `"".length`.
      inputCharacterLimit && [...val].length > inputCharacterLimit ? val.substring(0, inputCharacterLimit) : val;
      setInputValue(validInput);
      providedSetInputValue?.(validInput);
    }, [inputCharacterLimit, providedSetInputValue])
  });

  // Originally the combobox only used an input. When we added multiline support
  // we couldn't break the props referring to a specific type of focusable element
  // (like refs) so we will cast for now until we can ship a breaking change.
  // When we do that, we can add `| HTMLTextAreaElement` to `SelectProps`:
  // SelectProps<Option, HTMLButtonElement | HTMLInputElement | HTMLTextAreaElement>
  const downshiftTextAreaProps = downshiftInputProps;
  if (!isInteractive && downshiftInputProps) {
    downshiftInputProps.onChange = undefined;
    downshiftInputProps.onKeyDown = undefined;
    downshiftInputProps.readOnly = true;
  }
  const isContentBeforeVisible = contentBefore != undefined && (!inputValue || shouldContentBeforePersist);
  const {
    focusMode
  } = (0, _focusMode.useFocusMode)();
  (0, _react.useEffect)(() => {
    if (providedInputValue !== undefined && providedInputValue !== inputValue) {
      setInputValue(providedInputValue);
    }
  }, [inputValue, providedInputValue]);

  // If options changes, we'll have to re-run the filtering step and re-render.
  // This is usually for async-loaded options.
  const previousOptions = (0, _web.usePrevious)(options);
  (0, _react.useEffect)(() => {
    const isInitialRender = previousOptions === undefined;

    // NOTE: The `shouldFilterInitialInput` prop is crucial for managing the inputValue manually in a grid context.
    // When enabled, it allows manual control over when to trigger the filtering logic based on initial user input.
    // For instance, in a grid scenario, when a user overtypes a character to open the SelectComboBox,
    // the current inputValue is replaced by the overtype character. Enabling `shouldFilterInitialInput` ensures that
    // the options are filtered based on this overtype character
    const shouldFilter = isInitialRender && shouldFilterInitialInput || !isInitialRender;
    if (previousOptions !== options && shouldFilter) {
      if (inputValue) {
        doFilter(inputValue);
      } else {
        setFilteredOptions(null);
      }
    }
  }, [doFilter, options, previousOptions, inputValue, shouldFilterInitialInput]);
  (0, _useAutoHighlightWithLoading.useAutoHighlightWithLoading)(setHighlightedIndex, optionsToDisplay, isLoading, highlightFirstOptionAutomatically, isCreatable, inputValue);

  // This is extracted out since we have a bit of logic we wanted to use here. We had a bug where
  // devs using formatSelectedValue didn't have an absolutely-positioned wrapper to slot their
  // content into. This meant they had to add `position: absolute;` themselves which we didn't
  // want them to have to do. Unfortunately, we can't just add that style ourselves since it would
  // break their existing styles. So we have to do some trickery here to make sure we could allow
  // them to opt into it by using an optional prop that will go away in the next major release.
  const renderSelectedValue = () => {
    const formatted = selectedItem && !inputValue && (formatSelectedValue !== undefined ? formatSelectedValue({
      option: selectedOption
    }) :
    // The important thing here is that this default facade REQUIRES the wrapper below.
    // If the user doesn't specify a formatSelectedValue, we need to make sure we
    // apply the wrapper so it's positioned correctly.
    (0, _core.jsx)(_SelectedOptionFacade.SelectedOptionFacade
    // @ts-expect-error complaining that it doesn't know the string will be right
    , {
      variant: _selectV2Theme.selectBasicFontTheme.variant(themeMode),
      isContentBeforeVisible: isContentBeforeVisible,
      themeMode: themeMode,
      lineClamp: 1
    }, optionToString(selectedItem)));

    // Use the wrapper by default, or if the user has opted into it.
    const shouldUseWrapper = !formatSelectedValue || tempFormatSelectedValueWrapperFix;
    return shouldUseWrapper ? (0, _core.jsx)(_SelectedValueWrapper.SelectedValueWrapper, {
      isContentBeforeVisible: isContentBeforeVisible,
      themeMode: themeMode,
      style: {
        width: valueFormatWrapperWidth
      }
    }, formatted) : formatted;
  };

  /**
   * Downshift does a event.preventDefault on every key press that it cares about,
   * that's how the cursor doesn't move in the input when we navigate the menu
   * on single line select (which is a good thing)
   * That's bad news when we want to navigate the menu in multiline select,
   * so what this does is it determines if downshift should handle the input
   * based on the cursor position of the text area.
   */
  const handleMultilineKeyDown = e => {
    const inputEvent = e;

    // these are the keys that we may want to prevent downshift from handling
    const isConditionallyHandledKey = e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "Home" || e.key === "End" || e.key === "Enter";

    // only navigate menu up if highlighted index is not the first item
    const shouldNavigateMenuUp = (e.key === "ArrowUp" || e.key === "Home") && highlightedIndex > 0;

    // only navigate menu down if the cursor is at the end of the textarea
    const shouldNavigateMenuDown = (e.key === "ArrowDown" || e.key === "End") && localInputRef.current?.selectionStart === localInputRef.current?.value.length;

    // only select item if there is no other modifier that is being pressed along with the ENTER key
    const shouldSelectItem = e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey;
    if (shouldSelectItem || shouldNavigateMenuUp || shouldNavigateMenuDown || !isConditionallyHandledKey) {
      // let downshift handle the key press since it isn't an interaction that we care about preserving
      downshiftInputProps.onKeyDown?.(inputEvent);
    } else {
      // downshift automatically stops propagation events to the combobox
      // so we want to preserve them here
      commonKeyDownHandler(inputEvent);
    }
  };
  const handleContainerKeyDown = e => {
    if (e.key === "Tab") {
      isKeyboardNavigation.current = true;
    }
  };
  const handleSelectComboBoxFocus = (event, type) => {
    let isMenu, isInput, isToggleButton;
    if (typeof event?.relatedTarget?.getAttribute === "function") {
      isMenu = event?.relatedTarget?.getAttribute("data-client-id")?.includes("--menu");
      isInput = event?.relatedTarget?.getAttribute("data-client-id")?.includes("--input");
      isToggleButton = event?.relatedTarget === toggleButtonRef.current;
    }
    //rule out when the related target is the menu, input or toggle button
    //this way we are only firing the focus events when the container is focused/blurred
    if (!isMenu && !isToggleButton && !isInput) {
      if (type === "focus") {
        setIsFocused(true);
        isKeyboardNavigation.current = false;
        onFocus?.(event);
      } else {
        setIsFocused(false);
        onBlur?.(event);
      }
    }
    if (type === "blur" && isInput && isKeyboardNavigation.current) {
      //this happens when the user tabs out, so fire the onBlur event
      setIsFocused(false);
      onBlur?.(event);
    }

    //catch-all for focus styles only
    if (type === "focus") {
      setIsFocused(true);
    } else {
      setIsFocused(false);
    }
  };
  return (
    // TODO: use the Root component, or align the styles at least. Needs focus states!
    (0, _core.jsx)(_Root.Root, (0, _extends2.default)({
      inputFieldHeight: inputFieldHeight,
      "data-client-id": (0, _lodestarUtils.getClientId)(clientId, "-root"),
      className: className,
      isFocused: isFocused || isOpen,
      validationState: validationState || "rest",
      isReadonly: isReadonly,
      focusMode: focusMode,
      themeMode: themeMode,
      appearance: appearance,
      onKeyDown: handleContainerKeyDown,
      onBlur: e => handleSelectComboBoxFocus(e, "blur"),
      onFocus: e => handleSelectComboBoxFocus(e, "focus")
    }, getComboboxProps({
      ref: targetRef,
      "aria-owns": ariaOwns,
      onClick: e => {
        e.stopPropagation();
        if (isInteractive) {
          if (!isOpen) {
            localInputRef.current?.focus();
          }
          toggleMenu();
        }
      }
    })), (0, _core.jsx)(_HiddenInput.HiddenInput, {
      clientId: clientId,
      hiddenInputName: hiddenInputName,
      getHiddenInputValue: getHiddenInputValue,
      selectionType: selectedOption
    }), isContentBeforeVisible && (0, _core.jsx)(_ContentBeforeContainer.ContentBeforeContainer, {
      themeMode: themeMode
    }, contentBefore), isMultiline ? (0, _core.jsx)(_ComboBoxTextArea.ComboboxTextArea, (0, _extends2.default)({
      inputFieldHeight: inputFieldHeight,
      "data-client-id": (0, _lodestarUtils.getClientId)(clientId, "-input"),
      readOnly: isReadonly,
      rows: 1,
      disableManualResize: true,
      className: classes?.input
    }, downshiftTextAreaProps, {
      id: id,
      onKeyDown: handleMultilineKeyDown,
      onChange: (value, e) => {
        if (downshiftTextAreaProps.onChange) {
          downshiftTextAreaProps.onChange(e);
        }
      },
      textAreaStyles: {
        paddingLeft: isContentBeforeVisible ? "0px" : _selectV2Theme.selectV2Theme.input.padding.left(themeMode),
        // These negate the focus state padding changes in text area since
        // the Select is already handling that — we don't want to double it.
        paddingTop: _formInputTheme.formInputTheme.padding.default.rest(themeMode),
        paddingBottom: _formInputTheme.formInputTheme.padding.default.rest(themeMode),
        ...inputStyle
      },
      value: inputValue,
      themeMode: themeMode
    })) : (0, _core.jsx)(_ComboBoxInput.ComboBoxInput, (0, _extends2.default)({
      inputFieldHeight: inputFieldHeight,
      "data-client-id": (0, _lodestarUtils.getClientId)(clientId, "-input"),
      readOnly: isReadonly,
      isReadonly: isReadonly,
      className: classes?.input,
      isContentBeforeVisible: isContentBeforeVisible
    }, downshiftInputProps, {
      id: id,
      "aria-readonly": isReadonly,
      "aria-labelledby": ariaLabelledby,
      themeMode: themeMode,
      style: inputStyle
    })), selectedOption === null && !inputValue && placeholder && (0, _core.jsx)(_SelectPlaceholder.SelectPlaceholder, {
      inputFieldHeight: inputFieldHeight,
      hasContentBefore: !!contentBefore
    }, placeholder), isInteractive && (0, _core.jsx)(_Actions.Actions, {
      inputFieldHeight: inputFieldHeight,
      isReadonly: isReadonly,
      themeMode: themeMode
    }, (0, _core.jsx)(_ToggleButton.ToggleButton, (0, _extends2.default)({
      inputFieldHeight: inputFieldHeight
    }, getToggleButtonProps({
      // Don't let the container handle this.
      onClick: _utils.stopPropagationHandler
    }), {
      clientId: clientId,
      isReadonly: isReadonly,
      hideDropdownArrow: hideDropdownArrow,
      isHovered: isToggleHovered,
      isActive: isToggleActive,
      isOpen: isOpen,
      onMouseEnter: onTAMouseEnter,
      onMouseLeave: onTAMouseLeave,
      onMouseDown: onTAMouseDown,
      ref: toggleButtonRef
    })), isClearable && selectedOption && (0, _core.jsx)(_SelectClearButton.SelectClearButton, {
      inputFieldHeight: inputFieldHeight,
      refocusTargetElement: () => {
        localInputRef.current?.focus();
      },
      resetFiltering: resetFiltering,
      clearSelection: onChange,
      isMultiSelect: false
    })), renderSelectedValue(), (0, _core.jsx)(_SelectMenu.SelectMenu, {
      allSelected: false,
      enableSelectAll: false,
      flyoutProps: {
        ...flyoutProps,
        tabIndex: -1
      },
      formatOption: formatOption,
      getItemProps: getItemProps,
      getMenuProps: getMenuProps,
      className: classes?.menu,
      highlightedIndex: highlightedIndex,
      inputValue: inputValue,
      isLoading: isLoading,
      isOptionDisabled: isOptionDisabled,
      isOpen: isOpen,
      menuWidth: menuWidth,
      maxMenuHeight: maxMenuHeight,
      maxOptionsUntilVirtualization: maxOptionsUntilVirtualization,
      optionHeight: optionHeight,
      options: optionsToDisplay,
      optionToString: optionToString,
      selectedItems: [],
      selectedOption: selectedOption,
      targetWidth: targetWidth,
      getOptionKey: getOptionKey
    }))
  );
};
exports.SelectComboBox = SelectComboBox;
let warnOnceForDeprecatedSetMenuIsOpenProp;
//# sourceMappingURL=SelectComboBox.js.map